
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { createSearchCategoryUrl } from '@/utils/slugUtils';
import { 
  Car, 
  Smartphone, 
  Home, 
  ShoppingBag, 
  Briefcase, 
  Building, 
  Baby, 
  Bike, 
  Sofa,
  Utensils,
  Tv,
  BookOpen,
  Scissors,
  Globe,
  Heart,
  Headphones,
  ChevronRight,
  Store
} from 'lucide-react';
import { Container } from '@/components/ui/container';
import { Button } from '@/components/ui/button';

const Categories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .order('name')
          .limit(10);
        
        if (error) {
          throw error;
        }
        
        setCategories(data || []);
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError("Could not load categories");
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Get appropriate icon based on category name
  const getCategoryIcon = (categoryName: string) => {
    const iconSize = 28; // Larger icon size
    
    switch(categoryName.toLowerCase()) {
      case 'automjete':
        return <Car size={iconSize} className="w-7 h-7" />;
      case 'elektronikë':
        return <Smartphone size={iconSize} className="w-7 h-7" />;
      case 'shtëpi & mobilje':
      case 'pronë e patundshme':
      case 'patundshmëri':
        return <Building size={iconSize} className="w-7 h-7" />;
      case 'mobilje':
        return <Sofa size={iconSize} className="w-7 h-7" />;
      case 'modë & aksesorë':
      case 'veshje':
        return <ShoppingBag size={iconSize} className="w-7 h-7" />;
      case 'fëmijë & bebe':
        return <Baby size={iconSize} className="w-7 h-7" />;
      case 'sport & hobi':
        return <Bike size={iconSize} className="w-7 h-7" />;
      case 'ushqim & pije':
        return <Utensils size={iconSize} className="w-7 h-7" />;
      case 'teknologji':
      case 'kompjutera':
        return <Tv size={iconSize} className="w-7 h-7" />;
      case 'libra & revista':
        return <BookOpen size={iconSize} className="w-7 h-7" />;
      case 'shërbime':
        return <Scissors size={iconSize} className="w-7 h-7" />;
      case 'punë':
      case 'karrierë':
        return <Briefcase size={iconSize} className="w-7 h-7" />;
      case 'udhëtime':
        return <Globe size={iconSize} className="w-7 h-7" />;
      case 'shëndet & bukuri':
        return <Heart size={iconSize} className="w-7 h-7" />;
      case 'audio & muzikë':
        return <Headphones size={iconSize} className="w-7 h-7" />;
      default:
        return <ShoppingBag size={iconSize} className="w-7 h-7" />;
    }
  };

  const handleCategoryClick = (categoryId: number, categoryName: string) => {
    const searchCategoryUrl = createSearchCategoryUrl(categoryId, categoryName);
    navigate(searchCategoryUrl);
  };
  
  const handleViewAllCategories = () => {
    navigate('/search');
  };

  const handleNavigateToStores = () => {
    navigate('/stores');
  };

  if (loading) {
    return (
      <section className="py-10">
        <h2 className="text-2xl font-bold mb-6">Kategoritë</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="animate-pulse h-24 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-10">
        <h2 className="text-2xl font-bold mb-6">Kategoritë</h2>
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          {error}
        </div>
      </section>
    );
  }

  return (
    <section className="py-6">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold">Kategoritë</h2>
      </div>
      
      <div className="bg-green-50 rounded-xl p-6">
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
          {categories.slice(0, 10).map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category.id, category.name)}
              className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-green-100 transition-colors"
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 mb-2">
                <span className="text-green-600">
                  {getCategoryIcon(category.name)}
                </span>
              </div>
              <span className="text-sm font-medium text-center line-clamp-2">{category.name}</span>
            </button>
          ))}

          {/* Stores Button */}
          <button
            onClick={handleNavigateToStores}
            className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-green-100 transition-colors"
          >
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 mb-2">
              <Store className="text-green-600 w-7 h-7" />
            </div>
            <span className="text-sm font-medium text-center">Dyqanet</span>
          </button>
          
          {/* More Categories Button */}
          <button
            onClick={handleViewAllCategories}
            className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-green-100 transition-colors"
          >
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 mb-2">
              <ChevronRight className="text-green-600 w-7 h-7" />
            </div>
            <span className="text-sm font-medium">Më shumë</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Categories;
